const text = value => (value == null ? '' : String(value).trim())

export function readMapCoordinates (row) {
  const keys = Object.keys(row)
  // Prefer explicit coordinate names; retain support for existing prefixed columns.
  const find = (exact, pattern) =>
    keys.find(key => exact.includes(key.toLowerCase())) ||
    keys.find(key => pattern.test(key))
  const latitude = find(['lat', 'latitude'], /latitude$|(?:^|_)lat$/i)
  const longitude = find(
    ['lng', 'lon', 'long', 'longitude'],
    /longitude$|(?:^|_)(lng|lon|long)$/i
  )
  if (!latitude || !longitude || !text(row[latitude]) || !text(row[longitude]))
    return null
  if (
    !['string', 'number'].includes(typeof row[latitude]) ||
    !['string', 'number'].includes(typeof row[longitude])
  )
    return null
  const lat = Number(row[latitude])
  const lng = Number(row[longitude])
  return Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
    ? { lat, lng }
    : null
}

export function normalizeMapRows (rows, mode, sites = []) {
  if (!Array.isArray(rows))
    throw new Error('The location API must return a list.')
  const names = new Map(
    sites.map(row => [text(row.site_id), text(row.WindFarm)])
  )
  const seen = new Set()
  return rows.map(raw => {
    const siteId = text(raw.site_id)
    const assetId = mode === 'assets' ? text(raw.asset_id) : ''
    if (!siteId || (mode === 'assets' && !assetId))
      throw new Error('A map record is missing its site or asset ID.')
    const id = JSON.stringify([mode, siteId, assetId])
    if (seen.has(id))
      throw new Error(
        'The location list contains duplicate site/asset identities.'
      )
    seen.add(id)
    const health =
      text(mode === 'assets' ? raw.WTHealthStatus : raw.WFHealthStatus) ||
      'Not recorded'
    return {
      id,
      raw,
      siteId,
      assetId,
      mode,
      label:
        text(mode === 'assets' ? raw.Asset : raw.WindFarm) || assetId || siteId,
      siteLabel: names.get(siteId) || text(raw.WindFarmName) || siteId,
      health,
      healthTone: ['excellent', 'good', 'warning', 'poor', 'critical'].includes(
        health.toLowerCase()
      )
        ? health.toLowerCase()
        : 'unknown',
      lastInspection: text(
        mode === 'assets'
          ? raw.WTMostRecentInspection
          : raw.WFMostRecentInspection
      ),
      coordinates: readMapCoordinates(raw)
    }
  })
}

export const mapFilters = [
  { key: 'health', label: 'Recorded health', values: row => [row.health] },
  {
    key: 'mapping',
    label: 'Map location',
    values: row => [row.coordinates ? 'Mapped' : 'No coordinates']
  }
]
export const assetMapFilters = [
  ...mapFilters,
  {
    key: 'site',
    label: 'Site',
    values: row => [row.siteLabel + ' (' + row.siteId + ')']
  },
  {
    key: 'asset',
    label: 'Asset',
    values: row => [row.label + ' (' + row.assetId + ')']
  }
]
export function mapSearchText (row) {
  return [row.label, row.siteLabel, row.siteId, row.assetId, row.health].join(
    ' '
  )
}
export function mapDate (value) {
  if (!value) return 'Not recorded'
  return /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) ? value.slice(0, 10) : value
}
export function summarizeAssetInspections (rows) {
  if (!Array.isArray(rows))
    throw new Error('The inspection summary API must return a list.')
  const dates = rows
    .map(row => text(row.inspection_date))
    .filter(
      value =>
        /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) &&
        Number.isFinite(Date.parse(value)) &&
        new Date(value.slice(0, 10) + 'T00:00:00Z')
          .toISOString()
          .slice(0, 10) === value.slice(0, 10)
    )
  dates.sort((a, b) => Date.parse(b) - Date.parse(a))
  return {
    techniques:
      [
        ...new Set(rows.map(row => text(row.inspection_type)).filter(Boolean))
      ].join(' · ') || 'Not recorded',
    lastInspection: dates[0] ? mapDate(dates[0]) : 'Not recorded'
  }
}
